import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ImageUploader from './ImageUploader'
import { NOTICE_ICON_OPTIONS, legacyNoticeLines } from '../RaceLayout/TabPages/DetailsComponents/RaceGuide/DropBagInfo/notices'

export function SectionCard ({ title, children }) {
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>{title}</Typography>
      <Stack spacing={2}>{children}</Stack>
    </Paper>
  )
}

export function Txt ({ label, value, onChange, multiline, type = 'text', placeholder, minRows }) {
  return (
    <TextField
      label={label}
      value={value === undefined || value === null ? '' : value}
      onChange={e => {
        const v = e.target.value
        onChange(type === 'number' ? (v === '' ? '' : Number(v)) : v)
      }}
      type={type}
      placeholder={placeholder}
      multiline={multiline}
      minRows={minRows}
      size='small'
      fullWidth
    />
  )
}

export function SwitchRow ({ label, value, onChange }) {
  return (
    <FormControlLabel
      control={
        <Switch checked={!!value} onChange={e => onChange(e.target.checked)} size='small' />
      }
      label={label}
    />
  )
}

export function SelectRow ({ label, value, onChange, options }) {
  return (
    <FormControl size='small' fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value === undefined || value === null ? '' : value}
        onChange={e => onChange(e.target.value)}
      >
        {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
      </Select>
    </FormControl>
  )
}

const PRESET_COLORS = ['orange', '#aa0f0f', '#b2b216', '#0b478a']

export function ColorField ({ label, value, onChange }) {
  return (
    <Box>
      {label && <Typography variant='subtitle2' sx={{ mb: 1 }}>{label}</Typography>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {PRESET_COLORS.map(c => (
          <Box
            key={c}
            role='button'
            onClick={() => onChange(c)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              cursor: 'pointer',
              backgroundColor: c,
              border: value === c ? '3px solid #000' : '3px solid transparent',
              '&:hover': { transform: 'scale(1.1)' }
            }}
          />
        ))}
        <TextField
          type='color'
          size='small'
          value={/^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#000000'}
          onChange={e => onChange(e.target.value)}
          sx={{ width: 64 }}
        />
        <TextField
          label='Custom color'
          size='small'
          value={value || ''}
          placeholder='#ff6600'
          onChange={e => onChange(e.target.value)}
          sx={{ width: 180 }}
        />
      </Box>
    </Box>
  )
}

export function StringList ({ label, value, onChange, placeholder }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)

  const setItem = (index, v) => {
    const next = [...items]
    next[index] = v
    update(next)
  }

  const add = () => update([...items, ''])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      {label && <Typography variant='subtitle2' sx={{ mb: 1 }}>{label}</Typography>}
      {items.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            size='small'
            fullWidth
            value={item}
            placeholder={placeholder}
            onChange={e => setItem(i, e.target.value)}
          />
          <IconButton size='small' onClick={() => remove(i)}><DeleteIcon fontSize='small' /></IconButton>
        </Box>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add</Button>
    </Box>
  )
}

export function MapEditor ({ label, value, onChange, keyPlaceholder = 'key', valuePlaceholder = 'value' }) {
  const entries = value && typeof value === 'object' && !Array.isArray(value)
    ? Object.entries(value)
    : []

  const setKey = (index, k) => {
    const next = {}
    entries.forEach(([ek, ev], i) => {
      next[i === index ? k : ek] = ev
    })
    onChange(next)
  }

  const setValue = (index, v) => {
    const next = {}
    entries.forEach(([ek, ev], i) => {
      next[ek] = i === index ? v : ev
    })
    onChange(next)
  }

  const add = () => {
    const next = {}
    entries.forEach(([k, v]) => { next[k] = v })
    next[`key${Date.now()}`] = ''
    onChange(next)
  }

  const remove = (index) => {
    const next = {}
    entries.forEach(([k, v], i) => {
      if (i !== index) next[k] = v
    })
    onChange(next)
  }

  return (
    <Box>
      {label && <Typography variant='subtitle2' sx={{ mb: 1 }}>{label}</Typography>}
      {entries.map(([key, val], i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField size='small' value={key} placeholder={keyPlaceholder} sx={{ width: '30%' }} onChange={e => setKey(i, e.target.value)} />
          <TextField size='small' value={val === undefined || val === null ? '' : val} placeholder={valuePlaceholder} sx={{ width: '60%' }} onChange={e => setValue(i, e.target.value)} />
          <IconButton size='small' onClick={() => remove(i)}><DeleteIcon fontSize='small' /></IconButton>
        </Box>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add</Button>
    </Box>
  )
}

function ValueEditor ({ value, onChange }) {
  if (Array.isArray(value)) {
    return <StringList value={value} onChange={onChange} placeholder='item' />
  }
  if (value !== null && typeof value === 'object') {
    return <ObjectEditor value={value} onChange={onChange} />
  }
  return (
    <Txt
      value={value}
      onChange={onChange}
      type={typeof value === 'number' ? 'number' : 'text'}
      placeholder='value'
    />
  )
}

function ObjectEditor ({ value, onChange }) {
  const entries = value && typeof value === 'object' && !Array.isArray(value)
    ? Object.entries(value)
    : []

  const update = (next) => onChange(next)

  const setKey = (index, k) => {
    const next = {}
    entries.forEach(([ek, ev], i) => { next[i === index ? k : ek] = ev })
    update(next)
  }

  const setVal = (index, v) => {
    const next = {}
    entries.forEach(([ek, ev], i) => { next[ek] = i === index ? v : ev })
    update(next)
  }

  const remove = (index) => {
    const next = {}
    entries.forEach(([k, v], i) => { if (i !== index) next[k] = v })
    update(next)
  }

  const add = () => {
    const next = {}
    entries.forEach(([k, v]) => { next[k] = v })
    next[`key${Date.now()}`] = ''
    update(next)
  }

  return (
    <Box>
      {entries.map(([key, val], i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
          <TextField
            size='small'
            value={key}
            placeholder='key'
            sx={{ width: '28%' }}
            onChange={e => setKey(i, e.target.value)}
          />
          <Box sx={{ width: '60%' }}>
            <ValueEditor value={val} onChange={v => setVal(i, v)} />
          </Box>
          <IconButton size='small' onClick={() => remove(i)}><DeleteIcon fontSize='small' /></IconButton>
        </Box>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add</Button>
    </Box>
  )
}

export function ObjectList ({ label, value, onChange }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)

  const setItem = (index, v) => {
    const next = [...items]
    next[index] = v
    update(next)
  }

  const add = () => update([...items, {}])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      {label && <Typography variant='subtitle2' sx={{ mb: 1 }}>{label}</Typography>}
      {items.map((item, i) => (
        <Paper key={i} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
          <IconButton
            size='small'
            sx={{ position: 'absolute', top: 4, right: 4 }}
            onClick={() => remove(i)}
          >
            <DeleteIcon fontSize='small' />
          </IconButton>
          <ObjectEditor value={item} onChange={v => setItem(i, v)} />
        </Paper>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add</Button>
    </Box>
  )
}

const TRAVEL_VARIANTS = [
  { id: 'titleDescription', label: 'Title + Description' },
  { id: 'infoAction', label: 'Info + Action Link' },
  { id: 'scheduleTitle', label: 'Schedule Title' },
  { id: 'notes', label: 'Notes List' },
  { id: 'timeTable', label: 'Shuttle Timetable' }
]

function detectTravelVariant (item) {
  if (item?.timeTable) return 'timeTable'
  if (Array.isArray(item?.notes)) return 'notes'
  if (item?.scheduleTitle) return 'scheduleTitle'
  if (item?.info || item?.action) return 'infoAction'
  if (item?.title) return 'titleDescription'
  return 'titleDescription'
}

export function TravelParkingEditor ({ label, value, onChange }) {
  const isNull = value === null || value === undefined
  const items = Array.isArray(value) ? value : []

  const update = (next) => onChange(next)
  const setEnabled = (enabled) => onChange(enabled ? [] : null)

  const setVariant = (index, variant) => {
    const next = [...items]
    next[index] = { [variant]: '' }
    update(next)
  }

  const setField = (index, field, v) => {
    const next = [...items]
    next[index] = { ...next[index], [field]: v }
    update(next)
  }

  const add = () => update([...items, { title: '', description: '' }])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      {label && <Typography variant='subtitle2' sx={{ mb: 1 }}>{label}</Typography>}
      <FormControlLabel
        control={<Switch checked={!isNull} onChange={e => setEnabled(e.target.checked)} />}
        label={isNull ? 'No travel & parking info' : 'Has travel & parking info'}
      />
      {!isNull && (
        <Box sx={{ mt: 1 }}>
          {items.map((item, i) => {
            const variant = detectTravelVariant(item)
            return (
              <Paper key={i} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
                <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => remove(i)}>
                  <DeleteIcon fontSize='small' />
                </IconButton>
                <FormControl size='small' fullWidth sx={{ mb: 1 }}>
                  <InputLabel>Row type</InputLabel>
                  <Select label='Row type' value={variant} onChange={e => setVariant(i, e.target.value)}>
                    {TRAVEL_VARIANTS.map(v => <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>)}
                  </Select>
                </FormControl>
                {variant === 'titleDescription' && (
                  <Stack spacing={1}>
                    <Txt label='Title' value={item.title} onChange={v => setField(i, 'title', v)} />
                    <Txt label='Description' value={item.description} multiline minRows={2} onChange={v => setField(i, 'description', v)} />
                  </Stack>
                )}
                {variant === 'infoAction' && (
                  <Stack spacing={1}>
                    <Txt label='Info' value={item.info} multiline minRows={2} onChange={v => setField(i, 'info', v)} />
                    <Txt label='Action Label' value={item.action} onChange={v => setField(i, 'action', v)} />
                    <Txt label='Action URL' value={item.actionUrl} onChange={v => setField(i, 'actionUrl', v)} />
                  </Stack>
                )}
                {variant === 'scheduleTitle' && (
                  <Txt label='Schedule Title' value={item.scheduleTitle} onChange={v => setField(i, 'scheduleTitle', v)} />
                )}
                {variant === 'notes' && (
                  <StringList label='Notes' value={item.notes} onChange={v => setField(i, 'notes', v)} />
                )}
                {variant === 'timeTable' && (
                  <Stack spacing={1}>
                    <Txt label='Date' value={item.timeTable?.date} onChange={v => setField(i, 'timeTable', { ...item.timeTable, date: v })} />
                    <Txt label='Shuttle Time' value={item.timeTable?.shuttelTime} onChange={v => setField(i, 'timeTable', { ...item.timeTable, shuttelTime: v })} />
                    <Txt label='Zone' value={item.timeTable?.zone} onChange={v => setField(i, 'timeTable', { ...item.timeTable, zone: v })} />
                    <Txt label='Ceremony Time' value={item.timeTable?.ceremonyTime} onChange={v => setField(i, 'timeTable', { ...item.timeTable, ceremonyTime: v })} />
                    <Txt label='Start Time' value={item.timeTable?.startTime} onChange={v => setField(i, 'timeTable', { ...item.timeTable, startTime: v })} />
                    <Txt label='Location' value={item.timeTable?.location} onChange={v => setField(i, 'timeTable', { ...item.timeTable, location: v })} />
                  </Stack>
                )}
              </Paper>
            )
          })}
          <Button size='small' startIcon={<AddIcon />} onClick={add}>Add row</Button>
        </Box>
      )}
    </Box>
  )
}

const CHECKPOINT_FIELDS = [
  'cp', 'name', 'interDistKm', 'commDistKm', 'commDPlusM', 'commDMinusM', 'altitudeM',
  'crewSupport', 'services', 'location', 'cutOffTime'
]

export function CheckpointEditor ({ value, onChange }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)

  const setField = (index, field, v) => {
    const next = [...items]
    next[index] = { ...(next[index] || {}), [field]: v }
    update(next)
  }

  const add = () => {
    const next = [...items]
    next.push({ cp: '', name: '', interDistKm: '', commDistKm: '', commDPlusM: '', commDMinusM: '', altitudeM: '', crewSupport: '', services: '', location: '', cutOffTime: '' })
    update(next)
  }

  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Checkpoints</Typography>
      {items.map((item, i) => (
        <Paper key={i} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
          <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => remove(i)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
          <Stack spacing={1}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
              <Txt label='CP' value={item.cp} onChange={v => setField(i, 'cp', v)} />
              <Txt label='Name' value={item.name} onChange={v => setField(i, 'name', v)} />
            </Box>
            {CHECKPOINT_FIELDS.slice(2).map(field => (
              <Txt key={field} label={field} value={item[field]} onChange={v => setField(i, field, v)} />
            ))}
          </Stack>
        </Paper>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add checkpoint</Button>
    </Box>
  )
}

export function DropBagEditor ({ value, onChange, locations = [], distances = [] }) {
  const bag = value && typeof value === 'object' ? value : {}
  const set = (patch) => onChange({ ...bag, ...patch })

  const locationItems = Array.isArray(bag.locations) ? bag.locations : []
  const noticeItems = Array.isArray(bag.notices) ? bag.notices : legacyNoticeLines(bag.collectionInfo)

  const setLocation = (index, patch) => {
    const next = [...locationItems]
    next[index] = { ...(next[index] || {}), ...patch }
    set({ locations: next })
  }

  const addLocation = () => set({ locations: [...locationItems, { id: `drop-${Date.now()}`, title: '', locationName: '', validFor: [] }] })
  const removeLocation = (index) => set({ locations: locationItems.filter((_, i) => i !== index) })

  const setNotice = (index, patch) => {
    const next = [...noticeItems]
    next[index] = { ...(next[index] || {}), ...patch }
    set({ notices: next })
  }

  const addNotice = () => set({ notices: [...noticeItems, { icon: 'info', text: '' }] })
  const removeNotice = (index) => set({ notices: noticeItems.filter((_, i) => i !== index) })

  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Drop Bag Info</Typography>
      <Stack spacing={1}>
        <ImageUploader label='Drop bag photo' hint='Upload a photo of the drop bag. The existing photo is kept until you replace or remove it.' value={bag.image} onChange={v => set({ image: v })} />
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>General Info</Typography>
          <Stack spacing={1}>
            <Txt label='Participants' value={bag.generalInfo?.participants} onChange={v => set({ generalInfo: { ...bag.generalInfo, participants: v } })} />
            <Txt label='Bag Type' value={bag.generalInfo?.bagType} onChange={v => set({ generalInfo: { ...bag.generalInfo, bagType: v } })} />
            <Txt label='Capacity' value={bag.generalInfo?.capacity} onChange={v => set({ generalInfo: { ...bag.generalInfo, capacity: v } })} />
            <Txt label='Restrictions' value={bag.generalInfo?.restrictions} onChange={v => set({ generalInfo: { ...bag.generalInfo, restrictions: v } })} />
            <Txt label='Bib Instruction' value={bag.generalInfo?.bibInstruction} multiline minRows={2} onChange={v => set({ generalInfo: { ...bag.generalInfo, bibInstruction: v } })} />
          </Stack>
        </Box>
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Drop Bag Locations</Typography>
          {locationItems.map((item, i) => (
            <Paper key={`${i}-${item.id}`} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
              <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => removeLocation(i)}>
                <DeleteIcon fontSize='small' />
              </IconButton>
              <Stack spacing={1}>
                <FormControl size='small' fullWidth>
                  <InputLabel>Location</InputLabel>
                  <Select
                    label='Location'
                    value={item.locationName || ''}
                    onChange={e => setLocation(i, { locationName: e.target.value })}
                  >
                    <MenuItem value=''><em>None</em></MenuItem>
                    {locations.map(loc => <MenuItem key={loc.name} value={loc.name}>{loc.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Txt label='Title (optional, shown in bold)' value={item.title} onChange={v => setLocation(i, { title: v })} />
                <FormControl size='small' fullWidth>
                  <InputLabel>Valid for distances</InputLabel>
                  <Select
                    label='Valid for distances'
                    multiple
                    value={Array.isArray(item.validFor) ? item.validFor : []}
                    onChange={e => setLocation(i, { validFor: e.target.value })}
                    renderValue={(selected) => selected.map(d => `${d} km`).join(', ')}
                  >
                    {distances.map((d, di) => <MenuItem key={`${di}-${d.distance}`} value={d.distance}>{d.distance} km</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
            </Paper>
          ))}
          <Button size='small' startIcon={<AddIcon />} onClick={addLocation}>Add location</Button>
        </Box>
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Info &amp; Warning Lines</Typography>
          <Typography variant='caption' color='text.secondary' sx={{ mb: 1 }}>
            Add one line per entry. Pick the icon shown at the start of the line and type the text.
          </Typography>
          {noticeItems.map((item, i) => (
            <Paper key={i} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
              <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => removeNotice(i)}>
                <DeleteIcon fontSize='small' />
              </IconButton>
              <Stack spacing={1}>
                <FormControl size='small' fullWidth>
                  <InputLabel>Icon</InputLabel>
                  <Select
                    label='Icon'
                    value={item.icon || 'info'}
                    onChange={e => setNotice(i, { icon: e.target.value })}
                  >
                    {NOTICE_ICON_OPTIONS.map(icon => (
                      <MenuItem key={icon} value={icon}>{icon === 'info' ? 'Info' : 'Warning'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Txt label='Text' value={item.text} multiline minRows={2} onChange={v => setNotice(i, { text: v })} />
              </Stack>
            </Paper>
          ))}
          <Button size='small' startIcon={<AddIcon />} onClick={addNotice}>Add line</Button>
        </Box>
      </Stack>
    </Box>
  )
}

export function AccommodationEditor ({ value, onChange }) {
  const acc = value && typeof value === 'object' ? value : {}
  const chapters = Array.isArray(acc.chapters) ? acc.chapters : []
  const set = (patch) => onChange({ ...acc, ...patch })

  const setChapter = (index, patch) => {
    const next = [...chapters]
    next[index] = { ...(next[index] || {}), ...patch }
    set({ chapters: next })
  }

  const addChapter = () => set({ chapters: [...chapters, { title: '', description: '', hotels: [] }] })
  const removeChapter = (index) => set({ chapters: chapters.filter((_, i) => i !== index) })

  const setHotel = (ci, hi, patch) => {
    const next = [...chapters]
    const hotels = Array.isArray(next[ci].hotels) ? next[ci].hotels : []
    const nextHotels = [...hotels]
    nextHotels[hi] = { ...(nextHotels[hi] || {}), ...patch }
    next[ci] = { ...next[ci], hotels: nextHotels }
    set({ chapters: next })
  }

  const addHotel = (ci) => {
    const next = [...chapters]
    const hotels = Array.isArray(next[ci].hotels) ? next[ci].hotels : []
    next[ci] = { ...next[ci], hotels: [...hotels, { hotel: '', official: '', map: '', booking: '', stars: '' }] }
    set({ chapters: next })
  }

  const removeHotel = (ci, hi) => {
    const next = [...chapters]
    const hotels = Array.isArray(next[ci].hotels) ? next[ci].hotels : []
    next[ci] = { ...next[ci], hotels: hotels.filter((_, i) => i !== hi) }
    set({ chapters: next })
  }

  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Accommodation</Typography>
      {chapters.map((chapter, ci) => (
        <Paper key={ci} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
          <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => removeChapter(ci)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
          <Stack spacing={1}>
            <Txt label='Chapter Title' value={chapter.title} onChange={v => setChapter(ci, { title: v })} />
            <ImageUploader label='Image URL' value={chapter.image} onChange={v => setChapter(ci, { image: v })} />
            <Txt label='Description' value={chapter.description} multiline minRows={3} onChange={v => setChapter(ci, { description: v })} />
            <Txt label='URL Name' value={chapter.urlName} onChange={v => setChapter(ci, { urlName: v })} />
            <Txt label='URL' value={chapter.url} onChange={v => setChapter(ci, { url: v })} />
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>Hotels</Typography>
              {(chapter.hotels || []).map((hotel, hi) => (
                <Paper key={hi} variant='outlined' sx={{ p: 1.5, mb: 1.5, position: 'relative' }}>
                  <IconButton size='small' sx={{ position: 'absolute', top: 2, right: 2 }} onClick={() => removeHotel(ci, hi)}>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                  <Stack spacing={1}>
                    <Txt label='Hotel' value={hotel.hotel} onChange={v => setHotel(ci, hi, { hotel: v })} />
                    <Txt label='Official URL' value={hotel.official} onChange={v => setHotel(ci, hi, { official: v })} />
                    <Txt label='Map ID' value={hotel.map} onChange={v => setHotel(ci, hi, { map: v })} />
                    <Txt label='Booking' value={hotel.booking} onChange={v => setHotel(ci, hi, { booking: v })} />
                    <Txt label='Stars' type='number' value={hotel.stars} onChange={v => setHotel(ci, hi, { stars: v })} />
                  </Stack>
                </Paper>
              ))}
              <Button size='small' startIcon={<AddIcon />} onClick={() => addHotel(ci)}>Add hotel</Button>
            </Box>
          </Stack>
        </Paper>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={addChapter}>Add chapter</Button>
    </Box>
  )
}

export function LocationsEditor ({ value, onChange }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)

  const setItem = (index, patch) => {
    const next = [...items]
    next[index] = { ...(next[index] || {}), ...patch }
    update(next)
  }

  const add = () => update([...items, { name: '', googleUrl: '', gpxLocation: { lat: '', lng: '' } }])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Locations</Typography>
      {items.map((item, i) => (
        <Paper key={i} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
          <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => remove(i)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
          <Stack spacing={1}>
            <Txt label='Name' value={item.name} onChange={v => setItem(i, { name: v })} />
            <Txt label='Google Maps URL' value={item.googleUrl} onChange={v => setItem(i, { googleUrl: v })} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Txt label='Lat' value={item.gpxLocation?.lat} onChange={v => setItem(i, { gpxLocation: { ...item.gpxLocation, lat: v } })} />
              <Txt label='Lng' value={item.gpxLocation?.lng} onChange={v => setItem(i, { gpxLocation: { ...item.gpxLocation, lng: v } })} />
            </Box>
          </Stack>
        </Paper>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add location</Button>
    </Box>
  )
}

export function WelcomeEditor ({ value, onChange }) {
  const w = value && typeof value === 'object' ? value : {}
  const set = (patch) => onChange({ ...w, ...patch })

  return (
    <Stack spacing={1}>
      <Txt label='Title' value={w.title} onChange={v => set({ title: v })} />
      <ImageUploader label='Image' hint='Recommended resolution: 290 x 500' value={w.imageSrc} onChange={v => set({ imageSrc: v })} />
      <Txt label='Image Title' value={w.imageTitle} multiline minRows={2} onChange={v => set({ imageTitle: v })} />
      <Txt label='Columns' type='number' value={w.columns} onChange={v => set({ columns: v })} />
      <Txt label='Content' value={w.content} multiline minRows={6} onChange={v => set({ content: v })} />
    </Stack>
  )
}

export function TrackingInfoEditor ({ value, onChange }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)

  const setItem = (index, patch) => {
    const next = [...items]
    next[index] = { ...(next[index] || {}), ...patch }
    update(next)
  }

  const add = () => update([...items, { title: '', description: '', image: '', numbers: [] }])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Tracking Info</Typography>
      {items.map((item, i) => (
        <Paper key={i} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
          <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => remove(i)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
          <Stack spacing={1}>
            <Txt label='Title' value={item.title} onChange={v => setItem(i, { title: v })} />
            <Txt label='Description' value={item.description} multiline minRows={4} onChange={v => setItem(i, { description: v })} />
            <ImageUploader label='Image URL' value={item.image} onChange={v => setItem(i, { image: v })} />
            <StringList label='Numbers' value={item.numbers} onChange={v => setItem(i, { numbers: v })} />
          </Stack>
        </Paper>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add</Button>
    </Box>
  )
}

export function OtherRaceInfoEditor ({ value, onChange }) {
  return <ObjectList label='Other Race Info' value={value} onChange={onChange} />
}

export function PenaltyEditor ({ value, onChange }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)

  const setItem = (index, field, v) => {
    const next = [...items]
    next[index] = { ...(next[index] || {}), [field]: v }
    update(next)
  }

  const add = () => update([...items, { title: '', penalty: '' }])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      {items.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField size='small' fullWidth label='Title' value={item.title || ''} onChange={e => setItem(i, 'title', e.target.value)} />
          <TextField size='small' fullWidth label='Penalty' value={item.penalty || ''} onChange={e => setItem(i, 'penalty', e.target.value)} />
          <IconButton size='small' onClick={() => remove(i)}><DeleteIcon fontSize='small' /></IconButton>
        </Box>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add penalty</Button>
    </Box>
  )
}

export function BibCollectionEditor ({ value, onChange }) {
  const b = value && typeof value === 'object' ? value : {}
  const set = (patch) => onChange({ ...b, ...patch })
  return (
    <Stack spacing={1}>
      <Txt label='Pre-Race' value={b.preRace} multiline onChange={v => set({ preRace: v })} />
      <Txt label='Race Day' value={b.raceDay} multiline onChange={v => set({ raceDay: v })} />
    </Stack>
  )
}

export function ScheduleEditor ({ value, onChange }) {
  const s = value && typeof value === 'object' ? value : {}
  const set = (patch) => onChange({ ...s, ...patch })
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
      <Txt label='Start Time' value={s.startTime} onChange={v => set({ startTime: v })} />
      <Txt label='End Time' value={s.endTime} onChange={v => set({ endTime: v })} />
      <Txt label='Start Location' value={s.startLocationName} onChange={v => set({ startLocationName: v })} />
      <Txt label='Finish Location' value={s.finishLocationName} onChange={v => set({ finishLocationName: v })} />
    </Box>
  )
}

export function GearKeysEditor ({ label, value, onChange, availableKeys }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)
  const add = () => update([...items, ''])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      {label && <Typography variant='subtitle2' sx={{ mb: 1 }}>{label}</Typography>}
      {items.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <FormControl size='small' fullWidth>
            <InputLabel>Gear key</InputLabel>
            <Select
              label='Gear key'
              value={item || ''}
              onChange={e => {
                const next = [...items]
                next[i] = e.target.value
                update(next)
              }}
            >
              {availableKeys.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </Select>
          </FormControl>
          <IconButton size='small' onClick={() => remove(i)}><DeleteIcon fontSize='small' /></IconButton>
        </Box>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add</Button>
    </Box>
  )
}

export function PeopleEditor ({ label, value, onChange, showContact }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)

  const setItem = (index, patch) => {
    const next = [...items]
    next[index] = { ...(next[index] || {}), ...patch }
    update(next)
  }

  const add = () => update([...items, { name: '', instaSlug: '', avatar: '' }])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      {label && <Typography variant='subtitle2' sx={{ mb: 1 }}>{label}</Typography>}
      {items.map((item, i) => (
        <Paper key={i} variant='outlined' sx={{ p: 1.5, mb: 1.5, position: 'relative' }}>
          <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => remove(i)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, pr: 4, alignItems: 'center' }}>
            <TextField size='small' fullWidth label='Name' value={item.name || ''} onChange={e => setItem(i, { name: e.target.value })} />
            <TextField size='small' fullWidth label='Instagram' value={item.instaSlug || ''} onChange={e => setItem(i, { instaSlug: e.target.value })} />
            <TextField size='small' fullWidth label='Avatar' value={item.avatar || ''} onChange={e => setItem(i, { avatar: e.target.value })} />
          </Box>
          {showContact && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size='small' fullWidth label='Phone' value={item.phone || ''} onChange={e => setItem(i, { phone: e.target.value })} />
              <TextField size='small' fullWidth label='Email' value={item.email || ''} onChange={e => setItem(i, { email: e.target.value })} />
            </Box>
          )}
        </Paper>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add</Button>
    </Box>
  )
}

export function ContactsEditor ({ label, value, onChange }) {
  const items = Array.isArray(value) ? value : []
  const update = (next) => onChange(next)

  const setItem = (index, patch) => {
    const next = [...items]
    next[index] = { ...(next[index] || {}), ...patch }
    update(next)
  }

  const add = () => update([...items, { name: '', instaSlug: '', avatar: '', phone: '', email: '' }])
  const remove = (index) => update(items.filter((_, i) => i !== index))

  return (
    <Box>
      {label && <Typography variant='subtitle2' sx={{ mb: 1 }}>{label}</Typography>}
      {items.map((item, i) => (
        <Paper key={i} variant='outlined' sx={{ p: 2, mb: 2, position: 'relative' }}>
          <IconButton size='small' sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => remove(i)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
          <Stack spacing={1}>
            <Txt label='Name' value={item.name} onChange={v => setItem(i, { name: v })} />
            <Txt label='Instagram' value={item.instaSlug} onChange={v => setItem(i, { instaSlug: v })} />
            <Txt label='Avatar' value={item.avatar} onChange={v => setItem(i, { avatar: v })} />
            <Txt label='Phone' value={item.phone} onChange={v => setItem(i, { phone: v })} />
            <Txt label='Email' value={item.email} onChange={v => setItem(i, { email: v })} />
          </Stack>
        </Paper>
      ))}
      <Button size='small' startIcon={<AddIcon />} onClick={add}>Add contact</Button>
    </Box>
  )
}

export function KeyValueTable ({ label, data, emptyText = 'No rows' }) {
  return (
    <TableContainer component={Paper} variant='outlined'>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data && Object.keys(data).length > 0
            ? Object.entries(data).map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}><strong>{k}:</strong> {Array.isArray(v) ? v.join(', ') : v}</TableCell>
              </TableRow>
            ))
            : (
              <TableRow><TableCell color='text.secondary'>{emptyText}</TableCell></TableRow>
            )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
